import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { combineLatest, Observable, of } from 'rxjs'
import { catchError, filter, map, take, timeout } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { Booking } from 'src/app/features/booking/booking'
import { bookingActions } from 'src/app/features/booking/booking.actions'
import {
  ParticipantAdjustmentRow,
  ParticipantsAdjustmentAction,
  Participation,
  SaleItemParticipantAdjustment
} from 'src/app/features/booking/participation/participation'
import { participationActions } from 'src/app/features/booking/participation/participation.actions'
import { DeleteParticipationsModalComponent } from 'src/app/features/booking/participation/delete-participations-modal/delete-participations-modal.component'
import { participationSelectors } from 'src/app/features/booking/participation/participation.selectors'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { Component, Input, OnInit } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { paymentActions } from '../../payment/payment.actions'
import { paymentSelectors } from '../../payment/payment.selectors'
import { SaleItem } from '../../sale-item/sale-item'
import { saleItemActions } from '../../sale-item/sale-item.actions'
import { saleItemSelectors } from '../../sale-item/sale-item.selectors'
import { OrganizeBookingService } from '../organize-booking.service'
import {
  CellState,
  ParticipationByProductDate
} from '../participationbyproductdate'
import { TranslateService } from '@ngx-translate/core'

export enum SelectDateOptions {
  ONLY_FOR_SELECTED_DAY = 1,
  APPLY_FOR_WHOLE_STAY_ALREADY_BOOKED = 2,
  APPLY_FOR_WHOLE_STAY_ALL_DAY = 3,
  FOR_SELECTED_DAYS = 4
}

enum ChangeActions {
  ADD = 'add',
  DELETE = 'delete',
  CREATE = 'create',
  MOVE = 'move',
  NONE = 'none'
}

interface BookingChange {
  id: number
  date: string
  change: string
  action: ChangeActions
  participantsCountDifference?: number
  targetResourceId?: number
  numberOfParticipantsToMove?: number
}

@UntilDestroy()
@Component({
  selector: 'app-organize-bookings-modal',
  templateUrl: './organize-bookings-modal.component.html',
  styleUrls: ['./organize-bookings-modal.component.scss']
})
export class OrganizeBookingsModalComponent implements OnInit {
  @Input() key: string
  @Input() maxNumOfParticipants: number
  @Input() targetLinkedProductId: number
  @Input() participantCounts: { [key: string]: ParticipationByProductDate } = {}
  @Input() bookings: Booking[]
  @Input() participations: Participation[]
  @Input() productsInSelectedGroup: Product[]

  public products$: Observable<Product[]>
  public title$: Observable<string>
  public currentParticipantCount: number
  public originalParticipantCount: number
  public form: UntypedFormGroup

  public isCollapsed: boolean = false

  public selectedProductId: string
  public selectedDate: string
  public selectableDates = []
  public selectDateOptions = SelectDateOptions
  public changeActions = ChangeActions
  public changes: BookingChange[]
  public isSaving: boolean

  public maxNumOfParticipantsToShow: number
  public maxParticipantsOnResource: number

  public availableResourcesToMoveInto: Product[]

  private availableParticipantsPerDate = {}

  public selectDatesOption = [
    {
      id: SelectDateOptions.ONLY_FOR_SELECTED_DAY,
      title_en: 'Only for selected day',
      title_nl: 'Only for selected day'
    },
    {
      id: SelectDateOptions.APPLY_FOR_WHOLE_STAY_ALREADY_BOOKED,
      title_en: 'Apply for the whole stay(only for already booked days)',
      title_nl: 'Apply for the whole stay(only for already booked days)'
    },
    {
      id: SelectDateOptions.APPLY_FOR_WHOLE_STAY_ALL_DAY,
      title_en: 'Apply for the whole stay(for all day in the period)',
      title_nl: 'Apply for the whole stay(for all day in the period)'
    },
    {
      id: SelectDateOptions.FOR_SELECTED_DAYS,
      title_en: 'Select the days manually',
      title_nl: 'Select the days manually'
    }
  ]

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    private bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private actions$: Actions,
    private organizeBookingService: OrganizeBookingService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.maxParticipantsOnResource = this.maxNumOfParticipants
    this.maxNumOfParticipantsToShow = this.maxNumOfParticipants
    ;[this.selectedProductId, this.selectedDate] = this.key.split('_')

    this.availableResourcesToMoveInto = this.productsInSelectedGroup.filter(
      (product) => {
        const key = `${product.id}_${this.selectedDate}`
        return [CellState.AVAILABLE, CellState.BOOKED].includes(
          this.participantCounts[key].state
        )
      }
    )

    this.currentParticipantCount =
      this.participantCounts[this.key]?.currentParticipantCount ?? 0

    this.originalParticipantCount =
      this.participantCounts[this.key]?.originalParticipantCount ?? 0

    this.selectableDates = this.getKeysBySelectedDateOption(
      SelectDateOptions.APPLY_FOR_WHOLE_STAY_ALL_DAY
    ).map((key) => {
      const date = key.split('_')[1]
      return {
        date,
        longDate: moment(date).format('YYYY. MMMM DD')
      }
    })

    this.store
      .select(participationSelectors.selectAll)
      .pipe(take(1))
      .subscribe((participations) => {
        this.setAvailableParticipantsPerDate(participations)

        const filteredBookings = this.getFilteredBookingsByDateAndProduct(
          this.bookings,
          Number(this.selectedProductId),
          moment(this.selectedDate)
        )

        const filteredParticipations = participations.filter((p) =>
          filteredBookings.map((booking) => booking.id).includes(p.bookingId)
        )

        const availableParticipants =
          this.maxNumOfParticipants - (filteredParticipations?.length ?? 0)

        this.maxNumOfParticipants =
          this.currentParticipantCount + availableParticipants

        this.maxNumOfParticipantsToShow = this.maxNumOfParticipants

        this.form = this.fb.group({
          participantCount: [
            this.maxNumOfParticipants,
            [
              Validators.required,
              Validators.min(0)
              // Validators.max(this.maxNumOfParticipants)
            ]
          ],
          moveParticipants: [0, []],
          numberOfParticipantsToMove: [null, []],
          targetResourceId: [null, []],
          linkedProductId: [this.targetLinkedProductId, []],
          applyForWholeStay: [SelectDateOptions.ONLY_FOR_SELECTED_DAY, []],
          selectedDates: [[this.selectedDate], []]
        })
      })

    this.isSaving = false

    this.products$ = this.store
      .select(productSelectors.selectAll)
      .pipe(map((products) => products.filter((p) => !p.bookable)))

    this.title$ = this.store
      .select(productSelectors.selectById, +this.selectedProductId)
      .pipe(
        map((product) => {
          return `${product.description} - ${this.selectedDate}`
        })
      )
  }

  public onFormSubmit(): void {
    if (!this.validateForm()) {
      return
    }

    this.changes = this.getChanges()

    if (!Boolean(this.changes.find((change) => change.action != 'none'))) {
      this.notificationService.emit(
        'organizeBookingsModule.noChangesMade',
        'warning'
      )
      return
    }

    const message = this.translate.instant('saleModule.confirmChanges')
    if (!confirm(message)) {
      return
    }

    this.isSaving = true
    this.saveChanges(this.changes)
  }

  private getBookingDatesFromProduct(
    product: Product,
    date: string
  ): {
    startDate: string
    endDate: string
  } {
    let startDate = moment(`${date} ${product.defaultStartTime}`)

    let endDate = moment(`${date} ${product.defaultEndTime}`)

    if (startDate.isSameOrAfter(endDate)) {
      endDate.add(1, 'days')
    }

    return {
      startDate: startDate.format('YYYY-MM-DD HH:mm:SS'),
      endDate: endDate.format('YYYY-MM-DD HH:mm:SS')
    }
  }

  public cancel() {
    this.bsModalRef.hide()
  }

  public getChanges(): BookingChange[] {
    const changeEntries = {
      add: {
        en: 'Add %NUM participants to the booking',
        nl: 'Add %NUM participants to the booking'
      },
      remove: {
        en: 'Remove %NUM participants from the booking',
        nl: 'Remove %NUM participants from the booking'
      },
      create: {
        en: 'Create new bookin with %NUM participants',
        nl: 'Create new bookin with %NUM participants'
      },
      move: {
        en: 'Move %NUM participants to %DEST',
        nl: 'Move %NUM participants to %DEST'
      },
      noChanges: {
        en: 'No changes will be made on this day',
        nl: 'No changes will be made on this day'
      }
    }

    const {
      applyForWholeStay,
      participantCount,
      moveParticipants,
      targetResourceId,
      numberOfParticipantsToMove
    } = this.form.value

    const changes: BookingChange[] = []
    let changeId = 1

    // Move participants is selected
    if (moveParticipants == 1) {
      if (
        !targetResourceId ||
        !numberOfParticipantsToMove ||
        numberOfParticipantsToMove == 0
      ) {
        changes.push({
          id: changeId++,
          date: this.selectedDate,
          change: changeEntries.noChanges.en,
          action: ChangeActions.NONE
        })

        return changes
      }

      let resourceProductDescription = ''
      this.store
        .select(productSelectors.selectById, targetResourceId)
        .pipe(take(1))
        .subscribe((targetResource) => {
          resourceProductDescription = targetResource?.description ?? ''
        })

      changes.push({
        id: changeId++,
        date: this.selectedDate,
        change: changeEntries.move.en
          .replace('%NUM', numberOfParticipantsToMove)
          .replace('%DEST', resourceProductDescription),
        action: ChangeActions.MOVE,
        targetResourceId: Number(targetResourceId),
        numberOfParticipantsToMove
      })

      return changes
    }

    // Adjust participants is selected

    this.maxNumOfParticipantsToShow =
      +applyForWholeStay == SelectDateOptions.ONLY_FOR_SELECTED_DAY
        ? this.maxNumOfParticipants
        : this.maxParticipantsOnResource

    this.form.controls.participantCount.setValidators([
      Validators.required,
      Validators.min(0)
      // Validators.max(this.maxNumOfParticipantsToShow)
    ])

    this.form.controls.participantCount.updateValueAndValidity()

    const keys = this.getKeysBySelectedDateOption(+applyForWholeStay)

    for (const key of keys) {
      const [productId, date] = key.split('_')
      const cell = this.participantCounts[key]

      if (cell.currentParticipantCount == 0 && participantCount > 0) {
        changes.push({
          id: changeId++,
          date,
          change: changeEntries.create.en.replace('%NUM', participantCount),
          action: ChangeActions.CREATE,
          participantsCountDifference: participantCount
        })
        continue
      }

      const newParticipantCount = Math.min(
        participantCount,
        cell.currentParticipantCount + this.availableParticipantsPerDate[date]
      )

      const difference = Math.sign(
        cell.currentParticipantCount - participantCount
      )

      const participantsCountDifference = Math.abs(
        cell.currentParticipantCount - participantCount
      )

      switch (difference) {
        case -1:
          changes.push({
            id: changeId++,
            date,
            change: changeEntries.add.en.replace(
              '%NUM',
              `${participantsCountDifference}`
            ),
            action: ChangeActions.ADD,
            participantsCountDifference
          })
          break
        case 0:
          changes.push({
            id: changeId++,
            date,
            change: changeEntries.noChanges.en,
            action: ChangeActions.NONE,
            participantsCountDifference: 0
          })
          break
        case 1:
          changes.push({
            id: changeId++,
            date,
            change: changeEntries.remove.en.replace(
              '%NUM',
              `${participantsCountDifference}`
            ),
            action: ChangeActions.DELETE,
            participantsCountDifference
          })
          break
      }
    }

    return changes
  }

  private getKeysBySelectedDateOption(option: SelectDateOptions) {
    let keys = []

    switch (option) {
      case SelectDateOptions.ONLY_FOR_SELECTED_DAY:
        keys = [this.key]
        break

      case SelectDateOptions.APPLY_FOR_WHOLE_STAY_ALREADY_BOOKED:
        keys = Object.keys(this.participantCounts).filter(
          (key) =>
            key.includes(this.selectedProductId) &&
            [CellState.BOOKED].includes(this.participantCounts[key].state)
        )
        break

      case SelectDateOptions.APPLY_FOR_WHOLE_STAY_ALL_DAY:
        keys = Object.keys(this.participantCounts).filter(
          (key) =>
            key.includes(this.selectedProductId) &&
            [CellState.AVAILABLE, CellState.BOOKED].includes(
              this.participantCounts[key].state
            )
        )
        break

      case SelectDateOptions.FOR_SELECTED_DAYS:
        keys = Object.keys(this.participantCounts).filter((key) => {
          if (!key.includes(this.selectedProductId)) {
            return false
          }

          if (
            ![CellState.AVAILABLE, CellState.BOOKED].includes(
              this.participantCounts[key].state
            )
          ) {
            return false
          }

          const date = key.split('_')[1]
          return this.form.value.selectedDates.includes(date)
        })
        break
    }

    return keys
  }

  private validateForm(): boolean {
    if (!this.form.valid) {
      return false
    }

    const formValues = this.form.value

    if (formValues.moveParticipants == 1 && !formValues.targetResourceId) {
      this.notificationService.emit(
        'organizeBookingsModule.needToSelectTargetResource',
        'warning'
      )
      return false
    }

    if (!formValues.linkedProductId) {
      // TODO: mark linkedProductId field as invalid
      this.notificationService.emit(
        'organizeBookingsModule.needToSelectLinkedProduct',
        'warning'
      )
      return false
    }

    return true
  }

  private getUpdateParamsFromChange(
    change: BookingChange,
    productEntities: Dictionary<Product>,
    saleItemEntities: Dictionary<SaleItem>
  ): SaleItemParticipantAdjustment | undefined {
    const participantRows: ParticipantAdjustmentRow[] = []

    const key = this.organizeBookingService.getKey(
      this.selectedProductId,
      change.date
    )

    let targetSaleItemId = this.participantCounts[key]?.saleItemId

    if (!targetSaleItemId) {
      const participantCountKeysOnDate = Object.keys(
        this.participantCounts
      ).filter((key) => key.includes(change.date))

      const targetKey = participantCountKeysOnDate.find(
        (key) => this.participantCounts[key]?.saleItemId
      )

      if (!targetKey) {
        console.error('No saleitem found, terminating process')
        return
      }

      targetSaleItemId = this.participantCounts[targetKey].saleItemId
    }

    const newParticipantCount = this.form.value.participantCount

    const filteredBookings =
      this.organizeBookingService.getFilteredBookingsByDateAndProduct(
        this.bookings,
        productEntities[this.selectedProductId],
        new Date(change.date)
      )

    const targetSaleItem = saleItemEntities[targetSaleItemId] ?? null

    switch (change.action) {
      case ChangeActions.DELETE:
        break
      case ChangeActions.ADD:
        // * Need to add X participants to an existing booking

        if (!filteredBookings?.length) {
          this.notificationService.emit(
            `Cannot save changes: no bookings found on ${change.date}`,
            'error'
          )
          return
        }

        for (const p of Array(change.participantsCountDifference)) {
          participantRows.push({
            action: ParticipantsAdjustmentAction.ADD,
            destinationBookingId: filteredBookings[0]?.id
          })
        }
        break
      case ChangeActions.MOVE:
        // * Need to move X participants to a target resource

        if (!filteredBookings?.length) {
          this.notificationService.emit(
            `Cannot save changes: no bookings found on ${change.date}`,
            'error'
          )
          return
        }

        participantRows.push({
          action: ParticipantsAdjustmentAction.MOVE,
          sourceBookingId: filteredBookings[0]?.id,
          targetResourceId: change.targetResourceId,
          numberOfParticipantsToMove: change.numberOfParticipantsToMove
        })
        break

      case ChangeActions.CREATE:
        // * Need to add X participants, but there is no booking yet
        // * so backend will create it

        const targetLinkedProductId = +this.form.value.linkedProductId

        const linkedProduct = productEntities[targetLinkedProductId]

        const bookingDates = this.getBookingDatesFromProduct(
          linkedProduct,
          change.date
        )

        for (const p of Array(change.participantsCountDifference)) {
          participantRows.push({
            action: ParticipantsAdjustmentAction.ADD,
            targetProductId: +this.selectedProductId,
            targetLinkedProductId,
            startDate: bookingDates.startDate,
            endDate: bookingDates.endDate
          })
        }
        break
    }

    // Delete booking from store if all participants are removed
    if (
      change.action == ChangeActions.DELETE &&
      this.form.value.participantCount == 0
    ) {
      this.store.dispatch(
        bookingActions.removeOne({
          id: filteredBookings[0].id
        })
      )
    }

    return {
      saleItemId: targetSaleItem.id,
      participantRows
    }
  }

  private getParticipantIdsToCancel(
    change: BookingChange,
    saleItemEntities: Dictionary<SaleItem>
  ): number[] {
    const key = this.organizeBookingService.getKey(
      this.selectedProductId,
      change.date
    )

    let targetSaleItemId = this.participantCounts[key]?.saleItemId

    if (!targetSaleItemId) {
      const participantCountKeysOnDate = Object.keys(
        this.participantCounts
      ).filter((countKey) => countKey.includes(change.date))

      const targetKey = participantCountKeysOnDate.find(
        (countKey) => this.participantCounts[countKey]?.saleItemId
      )

      if (!targetKey) {
        return []
      }

      targetSaleItemId = this.participantCounts[targetKey].saleItemId
    }

    const targetSaleItem = saleItemEntities[targetSaleItemId] ?? null
    if (!targetSaleItem) {
      return []
    }

    const filteredBookings = this.getFilteredBookingsByDateAndProduct(
      this.bookings,
      Number(this.selectedProductId),
      moment(change.date)
    )

    const filteredParticipations = this.participations.filter((p) => {
      const saleItem = saleItemEntities[p.saleItemId]

      if (saleItem?.saleId != targetSaleItem.saleId) {
        return false
      }

      return filteredBookings.map((booking) => booking.id).includes(p.bookingId)
    })

    return filteredParticipations
      .map((p) => p.id)
      .sort((a, b) => b - a)
      .slice(0, change.participantsCountDifference)
  }

  private getFilteredBookingsByDateAndProduct(
    bookings: Booking[],
    productId: number,
    date: moment.Moment
  ): Booking[] {
    return bookings?.filter((booking) => {
      if (date.startOf('day').isAfter(moment(booking.startDate))) {
        return false
      }

      if (date.endOf('day').isBefore(moment(booking.startDate))) {
        return false
      }

      return (
        productId == booking.productId || productId == booking.linkedProductId
      )
    })
  }

  private setAvailableParticipantsPerDate(participations: Participation[]) {
    const dates = this.getKeysBySelectedDateOption(
      SelectDateOptions.APPLY_FOR_WHOLE_STAY_ALL_DAY
    ).map((key) => key.split('_')[1])

    for (const date of dates) {
      const filteredBookings = this.getFilteredBookingsByDateAndProduct(
        this.bookings,
        Number(this.selectedProductId),
        moment(date)
      )

      const filteredParticipations = participations.filter((p) =>
        filteredBookings.map((booking) => booking.id).includes(p.bookingId)
      )

      const availableParticipants =
        this.maxNumOfParticipants - (filteredParticipations?.length ?? 0)

      this.availableParticipantsPerDate[date] = availableParticipants
    }
  }

  private saveChanges(changes: BookingChange[]) {
    combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.store.select(saleItemSelectors.selectEntities)
    ])
      .pipe(take(1))
      .subscribe(([productEntities, saleItemEntities]) => {
        const updateParams: SaleItemParticipantAdjustment[] = []
        const deleteParticipationIds = new Set<number>()
        let saleId: number | null = null
        for (const change of changes) {
          if (change.action === ChangeActions.DELETE) {
            const participantIds = this.getParticipantIdsToCancel(
              change,
              saleItemEntities
            )
            participantIds.forEach((id) => deleteParticipationIds.add(id))

            if (!saleId) {
              const participation = this.participations.find((p) =>
                participantIds.includes(p.id)
              )
              saleId =
                saleItemEntities[participation?.saleItemId]?.saleId ?? null
            }
            continue
          }

          const params = this.getUpdateParamsFromChange(
            change,
            productEntities,
            saleItemEntities
          )

          if (!params?.participantRows?.length) {
            continue
          }

          updateParams.push(params)

          if (!saleId) {
            saleId = saleItemEntities[params.saleItemId]?.saleId ?? null
          }
        }

        if (!updateParams.length && !deleteParticipationIds.size) {
          console.warn(
            'Abort participant adjustment as no update param was received '
          )
          this.isSaving = false
          return
        }

        const dispatchNonDeleteAdjustments = () => {
          if (!updateParams.length || !saleId) {
            if (saleId) {
              this.refreshSalePayments(saleId)
            }
            this.notificationService.emit('alerts.success', 'success')
            this.cancel()
            this.isSaving = false
            return
          }

          this.store.dispatch(
            saleItemActions.updateManyBySaleId({
              saleId,
              updateParams: {
                suppressConfirmationEmails: true,
                participantAdjustments: [...updateParams]
              }
            })
          )

          this.actions$
            .pipe(
              ofType(saleItemActions.upsertMany),
              take(1),
              untilDestroyed(this),
              timeout(15000),
              catchError(() => of(null))
            )
            .subscribe((action) => {
              if (!action) {
                this.isSaving = false
                return
              }

              this.refreshSalePayments(saleId)
              this.notificationService.emit('alerts.success', 'success')
              this.cancel()
              this.isSaving = false
            })
        }

        if (!deleteParticipationIds.size) {
          dispatchNonDeleteAdjustments()
          return
        }

        const paidParticipationExists = [...deleteParticipationIds].some(
          (id) => {
            const participation = this.participations.find((p) => p.id === id)
            return Boolean(participation?.paid && participation?.amount > 0)
          }
        )

        const modalRef = this.modalService.show(
          DeleteParticipationsModalComponent,
          {
            initialState: {
              allowRefundMethodSelection: paidParticipationExists,
              showAllCancellationPolicies: true,
              showSendCancelationEmail: false,
              defaultSendCancelationEmail: false,
              productId:
                this.form.value.linkedProductId ??
                this.targetLinkedProductId ??
                +this.selectedProductId
            }
          }
        )

        this.modalService.onHide.pipe(take(1)).subscribe(() => {
          const modalResult = modalRef.content?.result
          if (!modalResult) {
            this.isSaving = false
            return
          }

          this.store.dispatch(
            participationActions.deleteMany({
              ids: [...deleteParticipationIds],
              refundPayMethod: modalResult.refundMethod,
              sendCancelationEmail: modalResult.sendCancelationEmail,
              cancelationPolicyUuid: modalResult.cancelationPolicyUuid
            })
          )

          this.actions$
            .pipe(
              ofType(
                participationActions.deleteManySuccess,
                participationActions.deleteManyFailed
              ),
              take(1),
              untilDestroyed(this)
            )
            .subscribe((action) => {
              if (action.type === participationActions.deleteManyFailed.type) {
                this.isSaving = false
                return
              }

              dispatchNonDeleteAdjustments()
            })
        })
      })
  }

  private refreshSalePayments(saleId: number): void {
    this.store
      .select(paymentSelectors.selectByFilter, {
        field: 'saleId',
        value: saleId
      })
      .pipe(take(1))
      .subscribe((payments) => {
        if (payments.length) {
          this.store.dispatch(
            paymentActions.removeMany({
              ids: payments.map((payment) => payment.id)
            })
          )
        }

        this.store.dispatch(
          paymentActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                saleId
              }
            })
          })
        )
      })
  }

  public getMaxNumOfParticipants(): Observable<number> {
    return this.store.select(productSelectors.selectEntities).pipe(
      map((productEntities) => {
        const product =
          productEntities[this.form.controls.targetResourceId.value]

        if (!product) {
          return 0
        }

        if (
          product.maxParticipants === null ||
          product.maxParticipants === undefined
        ) {
          return this.currentParticipantCount ?? 0
        }

        return product.maxParticipants
      })
    )
  }
}
